import { memo, useEffect, useMemo, useRef } from 'react';
import Underline from './Underline';
import { createRange, getUnderlinesInfo } from './utils';
import useStore from 'store';
import { useInlineMessageGatedByPro } from 'store/selectors/message';

const UnderlinesForMessage = memo(function UnderlineMessage({
  srId,
  messageId,
  active,
}: {
  srId: string;
  messageId: string;
  active: boolean;
}) {
  const underlines = useUnderlines(srId, messageId);

  return (
    <>
      {underlines.map((underline) => {
        const { top, left, width, height } = underline;
        const key = `${top + left + width + height}`;
        return (
          <Underline
            top={top}
            left={left}
            width={width}
            height={height}
            messageId={messageId}
            active={active}
            key={key}
          />
        );
      })}
    </>
  );
});

function useUnderlines(srId: string, messageId: string) {
  const elementRef = useRef<HTMLElement | undefined>(
    useStore.getState().elements.get(srId)
  );
  const message = useInlineMessageGatedByPro(messageId);

  // Conditionally get message info
  const start = message?.offsets?.start;
  const end = message?.offsets?.end;

  // Store the element in a ref to avoid unnecessary renders
  useEffect(
    () =>
      useStore.subscribe(
        (element: HTMLElement | undefined) => (elementRef.current = element),
        (state) => state.elements.get(srId)
      ),
    [srId]
  );

  // Conditionally get element info
  const element = elementRef.current;
  let top: number | undefined = undefined;
  let left: number | undefined = undefined;
  if (element) {
    const rect = element.getBoundingClientRect();
    top = rect.top;
    left = rect.left;
  }

  const range = useMemo(
    () => createRange(element, start, end),
    [element, start, end]
  );
  const underlines = useMemo(
    () => getUnderlinesInfo(range, top, left),
    [range, top, left]
  );

  return underlines;
}

export default UnderlinesForMessage;
